/**
 * usePasswordFormSubmit — l'envoi des deux formulaires de mot de passe d'avant connexion
 * (« Mot de passe oublié ? », « Nouveau mot de passe »), comme leurs deux écrans legacy :
 *  - un envoi à la fois, par `apiRequestWithPrerequest` (le cookie CSRF d'abord) ;
 *  - un premier envoi montre la validation, qui suit ensuite la frappe ; un envoi efface le refus
 *    précédent, et rien ne part tant qu'un champ est faux ;
 *  - réussi → les félicitations, puis l'écran `congrats`, qui reste legacy ;
 *  - un refus se dit dans les mots du serveur (`authErrorText`).
 *
 * Chaque écran garde ses champs et sa règle ; il passe ici le corps et le verdict de sa règle.
 */
import { useState } from "react"
import { Keyboard } from "react-native"
import { useNavigation } from "@react-navigation/native"
import { useDispatch } from "react-redux"

import { putCongrats } from "../../../../../data/redux/actions/navigation"
import { genericCongrats } from "../../../view-models/auth/Password/passwordFormViewModel"
import { authErrorText } from "../../hooks/apiErrorMessage"
import { useApiMutation, type UseApiMutationOptions } from "../../hooks/useApiMutation"

type PasswordFormSubmitOptions = {
    service: UseApiMutationOptions["service"]
    /** Les félicitations d'un envoi réussi. */
    done: { title: string; text: string }
    /** La phrase d'un refus sans message lisible. */
    fallback: string
}

export function usePasswordFormSubmit({ service, done, fallback }: PasswordFormSubmitOptions) {
    const navigation = useNavigation<{ replace: (name: string) => void }>()
    const dispatch = useDispatch()
    const [submitted, setSubmitted] = useState(false)
    const [serverError, setServerError] = useState<string | null>(null)

    const { start, requesting } = useApiMutation({
        service,
        prerequest: true,
        onSuccess: () => {
            dispatch(putCongrats(genericCongrats(done)) as never)
            navigation.replace("congrats")
        },
        onError: (_message, error) => setServerError(authErrorText(error, fallback)),
    })

    const submit = (body: Record<string, unknown>, invalid: boolean) => {
        if (requesting) return
        setSubmitted(true)
        setServerError(null)
        if (invalid) return
        Keyboard.dismiss()
        start({}, body)
    }

    return { submitted, serverError: serverError ?? undefined, submitting: requesting, submit }
}
